import { useEffect, useRef, useState, useCallback } from "react";
import "./patientReceiptStyles.css";
import converter from "number-to-words";
import { useQuery } from "@tanstack/react-query";
import { fetchPatientReceiptData } from "services/patientappointmentdetails";
import { DEFAULT_TOTAL_RECORDS, STALE_TIME } from "utils/constants";
import usePagination from "hooks/usePagination";
import Loader from "components/Common/loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useSearchParams } from "react-router-dom";
import zoneLocalStorageId from "utils/zone";
import { fetchZoneLogoById } from "services/zone";
import { useSelector } from "react-redux";

const NewRegistrationPatientReceipt = ({
  appointmentId: appointmentIdProp,
  patientId: patientIdProp,
  membershipCardNo: membershipCardNoProp,
  setMembershipCardState,
  hidden = false,
  onDownloadComplete,
}) => {
  const [searchParams] = useSearchParams();
  const hasDownloaded = useRef(false);
  const receiptRef = useRef(null);
  const [isComponentReady, setIsComponentReady] = useState(false);
  const { currentPage, updateTotalRecords } = usePagination();
  const [billAmt, setBillAmt] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfCaptureMode, setIsPdfCaptureMode] = useState(false);
  const receiptData = JSON.parse(localStorage.getItem("receiptPatient"));

  const appointmentId = appointmentIdProp || searchParams.get("appointmentId");
  const patientId = patientIdProp || searchParams.get("patientId");
  const membershipCardNo =
    membershipCardNoProp || searchParams.get("membershipCardNo");

  const { user } = useSelector((state) => state.jwtAuthreducer);

  const formatUnixTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options).replace(",", "");
  };

  function convertEpochToDate(epoch) {
    if (!epoch) return "";
    const date = new Date(epoch * 1000);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  function getDaysFromEpoch(epoch, options = {}) {
    if (!epoch || isNaN(Number(epoch))) {
      return options.defaultValue !== undefined ? options.defaultValue : 0;
    }
    const inputDate = new Date(Number(epoch) * 1000);
    const currentDate = new Date();
    const inputDateStart = new Date(
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate(),
    );
    const currentDateStart = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate(),
    );
    const diffDays = Math.ceil(
      (inputDateStart - currentDateStart) / (1000 * 60 * 60 * 24),
    );
    return diffDays;
  }

  const { data: zoneData, isLoading: isZoneLoading } = useQuery(
    ["fetch-zone-logo-by-id", zoneLocalStorageId()],
    () => fetchZoneLogoById(zoneLocalStorageId()),
    {
      enabled: !!zoneLocalStorageId(),
      staleTime: STALE_TIME,
      select: (res) => res || [],
      onSuccess: (res) => {
        console.log("Zone data fetched:", res);
      },
      onError: (err) => {
        console.error("Error fetching zone data:", err);
      },
    },
  );

  const {
    data: cashReceiptData,
    isLoading,
    refetch,
  } = useQuery(
    ["fetch-cash-patient-receipt"],
    () =>
      fetchPatientReceiptData({
        appointment_id: appointmentId,
      }),
    {
      staleTime: STALE_TIME,
      onSuccess: (res) => {
        updateTotalRecords(res?.data?.additional_data?.total_count);
      },
      select: (res) => {
        const response = res?.data?.data;
        if (response && response.length) {
          res.data.data = response.map((x, index) => ({
            ...x,
            _index: (currentPage - 1) * DEFAULT_TOTAL_RECORDS + index + 1,
          }));
        }
        return res;
      },
      onError: (res) => {
        console.error("Error fetching payments:", res);
      },
      enabled: !!appointmentId,
    },
  );

  useEffect(() => {
    if (cashReceiptData && cashReceiptData.data[0]) {
      const appointment_investigation_obj =
        cashReceiptData.data[0].appointment_investigation_obj;
      const uniquePackages = {};
      let noPackageTotal = 0;
      appointment_investigation_obj.forEach((item) => {
        const packageId = item.package_id;
        const cost = item.package?.package_cost || item.mrp || 0;
        if (packageId) {
          if (!uniquePackages[packageId]) {
            uniquePackages[packageId] = cost;
          }
        } else {
          noPackageTotal += cost;
        }
      });
      const packageTotal = Object.values(uniquePackages).reduce(
        (sum, cost) => sum + cost,
        0,
      );
      const totalBillAmt = packageTotal + noPackageTotal;
      setBillAmt(totalBillAmt);
    }
  }, [cashReceiptData]);

  useEffect(() => {
    if (
      cashReceiptData &&
      billAmt >= 0 &&
      receiptRef.current &&
      !isZoneLoading
    ) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsComponentReady(true);
        });
      });
    }
  }, [cashReceiptData, billAmt, isZoneLoading]);

  function convertSecondsToAge(seconds) {
    const secondsInYear = 31536000;
    const secondsInMonth = 2629746;
    const secondsInDay = 86400;
    let remaining = Number(seconds) || 0;
    const years = Math.floor(remaining / secondsInYear);
    remaining %= secondsInYear;
    const months = Math.floor(remaining / secondsInMonth);
    remaining %= secondsInMonth;
    const days = Math.floor(remaining / secondsInDay);
    return { years, months, days };
  }

  const rowCashReceiptData = cashReceiptData?.data[0];

  const {
    appointment_detail_obj = [],
    appointment_investigation_obj = [],
    appoint_packages = [],
    franchise_obj = {},
    patient_obj = {},
    payment_mode_obj = {},
    appointment_obj = {},
    zone_obj = {},
    refer_doctor_obj = {},
  } = cashReceiptData?.data[0] || {};

  const ageDetails = convertSecondsToAge(patient_obj?.age || 0);

  const capitalizeWords = (str) =>
    (str || "").replace(/\b\w/g, (char) => char.toUpperCase());

  const ensureImagesLoaded = (el) => {
    const images = Array.from(el.querySelectorAll("img"));
    return Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (!img) return resolve();
            if (img.complete && img.naturalWidth !== 0) return resolve();
            const onLoad = () => {
              cleanup();
              resolve();
            };
            const onError = () => {
              cleanup();
              resolve();
            };
            const cleanup = () => {
              img.removeEventListener("load", onLoad);
              img.removeEventListener("error", onError);
            };
            img.addEventListener("load", onLoad);
            img.addEventListener("error", onError);
          }),
      ),
    );
  };

  const downloadPDF = useCallback(
    async (skipAutoClose) => {
      try {
        setIsDownloading(true);
        setIsPdfCaptureMode(true);

        await new Promise((r) => setTimeout(r, 150));

        const receiptElement = receiptRef.current;
        if (!receiptElement)
          throw new Error("Receipt element reference missing");

        await ensureImagesLoaded(receiptElement);

        const footerElement = receiptElement.querySelector(".receipt-footer");
        const addressElement =
          receiptElement.querySelector(".pdf-address-field");
        const contactRow = receiptElement.querySelector(".contact-row");
        const downloadBtn = receiptElement.querySelector(".download-btn");

        const originalStyles = {
          width: receiptElement.style.width,
          minWidth: receiptElement.style.minWidth,
          footerDisplay: footerElement?.style.display,
          addressDisplay: addressElement?.style.display,
          contactDisplay: contactRow?.style.display,
          downloadBtnDisplay: downloadBtn?.style.display,
        };

        if (downloadBtn) downloadBtn.style.display = "none";
        if (footerElement) footerElement.style.display = "none";
        if (addressElement) addressElement.style.display = "none";
        if (contactRow) contactRow.style.display = "none";

        receiptElement.style.width = "794px";
        receiptElement.style.minWidth = "794px";

        await new Promise((r) => setTimeout(r, 200));

        const elementWidth = Math.max(receiptElement.scrollWidth, 794);
        const elementHeight = Math.max(receiptElement.scrollHeight, 1122);

        const canvas = await html2canvas(receiptElement, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: "#ffffff",
          width: elementWidth,
          height: elementHeight,
          windowWidth: elementWidth,
          windowHeight: elementHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false,
        });

        receiptElement.style.width = originalStyles.width || "";
        receiptElement.style.minWidth = originalStyles.minWidth || "";
        if (footerElement)
          footerElement.style.display = originalStyles.footerDisplay || "block";
        if (addressElement)
          addressElement.style.display =
            originalStyles.addressDisplay || "block";
        if (contactRow)
          contactRow.style.display = originalStyles.contactDisplay || "flex";
        if (downloadBtn)
          downloadBtn.style.display =
            originalStyles.downloadBtnDisplay || "block";

        const imgData = canvas.toDataURL("image/png", 1.0);
        const pdf = new jsPDF("p", "mm", "a4");

        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const imgWidthMM = pdfWidth;
        const imgHeightMM = (imgProps.height * imgWidthMM) / imgProps.width;

        let heightLeft = imgHeightMM;
        let position = 0;

        pdf.addImage(imgData, "PNG", 0, position, imgWidthMM, imgHeightMM);
        heightLeft -= pdfHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeightMM;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, imgWidthMM, imgHeightMM);
          heightLeft -= pdfHeight;
        }

        const BRAND_RGB = [91, 175, 214];
        const mobile = rowCashReceiptData?.zone_obj?.mobile_number || "";
        const email = rowCashReceiptData?.zone_obj?.email || "";
        const website = rowCashReceiptData?.zone_obj?.website || "";
        const address = rowCashReceiptData?.zone_obj?.address || "";
        const footerBarH = address ? 18 : 12;

        const pageCount = pdf.getNumberOfPages();
        pdf.setPage(pageCount);

        const barY = pdfHeight - footerBarH;
        pdf.setFillColor(...BRAND_RGB);
        pdf.rect(0, barY, pdfWidth, footerBarH, "F");
        pdf.setDrawColor(255, 255, 255);
        pdf.setLineWidth(0.3);
        pdf.line(0, barY, pdfWidth, barY);

        const contactItems = [];
        if (mobile) contactItems.push({ l: "M", v: mobile });
        if (email) contactItems.push({ l: "E", v: email });
        if (website) contactItems.push({ l: "W", v: website });

        if (contactItems.length > 0) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8.5);
          const circleW = 5;
          pdf.setFont("helvetica", "bold");
          const sepW = pdf.getTextWidth("  |  ");
          pdf.setFont("helvetica", "normal");
          const totalW = contactItems.reduce(
            (sum, it, i) =>
              sum +
              circleW +
              pdf.getTextWidth(it.v) +
              (i < contactItems.length - 1 ? sepW : 0),
            0,
          );

          let curX = (pdfWidth - totalW) / 2;
          const rowY = barY + 7;
          contactItems.forEach((it, i) => {
            pdf.setFillColor(255, 255, 255);
            pdf.circle(curX + 1.8, rowY - 1.2, 1.8, "F");
            pdf.setTextColor(...BRAND_RGB);
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(6);
            pdf.text(it.l, curX + 0.9, rowY - 0.3);
            pdf.setTextColor(255, 255, 255);
            pdf.setFont("helvetica", "normal");
            pdf.setFontSize(8.5);
            pdf.text(it.v, curX + 4.6, rowY);
            curX += circleW + pdf.getTextWidth(it.v);
            if (i < contactItems.length - 1) {
              pdf.setFont("helvetica", "bold");
              pdf.text("  |  ", curX, rowY);
              curX += sepW;
            }
          });
        }

        if (address) {
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(8);
          pdf.setTextColor(255, 255, 255);
          const addrLine = pdf.splitTextToSize(
            `Addr: ${address}`,
            pdfWidth - 20,
          )[0];
          pdf.text(
            addrLine,
            (pdfWidth - pdf.getTextWidth(addrLine)) / 2,
            barY + 14,
          );
        }
        pdf.setTextColor(0, 0, 0);

        const patientName = patient_obj?.patient_name || "Patient";
        const currentDate = new Date().toISOString().split("T")[0];

        const pdfBlob = pdf.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");

        if (onDownloadComplete && !skipAutoClose) {
          try {
            onDownloadComplete();
          } catch (e) {}
        }
      } catch (error) {
        console.error("Error generating PDF:", error);
        alert("Error generating PDF: " + (error.message || error));
      } finally {
        setIsDownloading(false);
        setIsPdfCaptureMode(false);
      }
    },
    [rowCashReceiptData, patient_obj, onDownloadComplete],
  );

  const packageLookup = appoint_packages.reduce((acc, pkg) => {
    acc[pkg.package_id] = pkg;
    return acc;
  }, {});

  const groupedData = appointment_investigation_obj.reduce(
    (acc, item, index) => {
      const packageId = item.package_id;
      if (packageId) {
        const packageInfo = packageLookup[packageId] || {};
        if (!acc[packageId]) {
          acc[packageId] = {
            package: item.package,
            investigations: [],
            departments: new Set(),
            delivery_times: new Set(),
            mrp: packageInfo.mrp || 0,
            rate: packageInfo.rate || 0,
            package_cost: packageInfo.package_cost || packageInfo.mrp || 0,
            discount: item.discount || 0,
          };
        }
        acc[packageId].investigations.push(item.investigation);
        if (item.investigation?.lab_department?.department) {
          acc[packageId].departments.add(
            item.investigation.lab_department.department,
          );
        }
        if (item.delivery_time) {
          acc[packageId].delivery_times.add(
            convertEpochToDate(item.delivery_time),
          );
        }
      } else {
        const uniqueKey = `no_package_${index}`;
        acc[uniqueKey] = {
          package: null,
          investigations: [item.investigation],
          departments: new Set(
            item.investigation?.lab_department?.department
              ? [item.investigation.lab_department.department]
              : [],
          ),
          delivery_times: new Set(
            item.delivery_time ? [convertEpochToDate(item.delivery_time)] : [],
          ),
          mrp: item.mrp || 0,
          rate: item.rate || 0,
          discount: item.discount || 0,
          package_cost: item.rate || 0,
        };
      }
      return acc;
    },
    {},
  );

  const totalItemDiscounts = Object.values(groupedData).reduce((sum, data) => {
    const discountAmount = data.mrp - data.package_cost;
    return sum + (discountAmount > 0 ? discountAmount : 0);
  }, 0);

  const totalPaidAmount = appointment_detail_obj[0]?.received_amount || 0;

  useEffect(() => {
    if (
      hidden &&
      isComponentReady &&
      !hasDownloaded.current &&
      !isDownloading &&
      !isZoneLoading
    ) {
      const timer = setTimeout(() => {
        hasDownloaded.current = true;
        downloadPDF()
          .then(() => {
            setMembershipCardState?.(undefined);
            onDownloadComplete?.();
          })
          .catch((err) => {
            console.error("PDF download failed:", err);
          });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [
    hidden,
    isComponentReady,
    isDownloading,
    isZoneLoading,
    downloadPDF,
    onDownloadComplete,
    setMembershipCardState,
  ]);

  if (!cashReceiptData || isLoading) {
    return <Loader isLoading={isLoading} />;
  }

  const containerStyle = hidden
    ? {
        position: "fixed",
        left: 0,
        top: 0,
        pointerEvents: "none",
        width: "1000px",
        minWidth: "1000px",
        zIndex: -1,
        opacity: 0,
      }
    : {};

  const BRAND = "#5bafd6";

  const styles = {
    pageWrap: {
      background: "#f0f4f8",
      minHeight: "100vh",
      padding: "0",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      overflowY: "auto",
    },
    card: {
      maxWidth: "794px",
      margin: "0 auto",
      background: "#ffffff",
      borderRadius: "0 0 10px 10px",
      boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      overflow: "visible",
    },
    header: {
      background: BRAND,
      padding: "0",
      position: "relative",
    },
    headerTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "10px 20px 8px",
      gap: "10px",
      flexWrap: "wrap",
    },
    logoBox: {
      background: "#ffffff",
      borderRadius: "8px",
      padding: "5px 10px",
      display: "flex",
      alignItems: "center",
      boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
    },
    headerCenter: { flex: 1, textAlign: "center" },
    headerTitle: {
      color: "#ffffff",
      fontSize: "17px",
      fontWeight: "800",
      letterSpacing: "2px",
      textTransform: "uppercase",
      margin: 0,
      textShadow: "0 1px 4px rgba(0,0,0,0.15)",
    },
    headerSubtitle: {
      color: "rgba(255,255,255,0.85)",
      fontSize: "10px",
      letterSpacing: "1px",
      marginTop: "2px",
    },
    badgePill: {
      background: "rgba(255,255,255,0.22)",
      border: "1px solid rgba(255,255,255,0.45)",
      color: "#fff",
      borderRadius: "20px",
      padding: "3px 11px",
      fontSize: "10px",
      fontWeight: "600",
      letterSpacing: "0.8px",
      whiteSpace: "nowrap",
    },
    headerDivider: {
      height: "3px",
      background: "linear-gradient(90deg, #f0a500, #ffcd57, #f0a500)",
    },
    body: { padding: "12px 20px" },
    sectionLabel: {
      fontSize: "9px",
      fontWeight: "700",
      color: BRAND,
      letterSpacing: "1.2px",
      textTransform: "uppercase",
      borderBottom: "2px solid #e5edf7",
      paddingBottom: "3px",
      marginBottom: "6px",
    },
    infoGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "0 24px",
      marginBottom: "12px",
    },
    infoRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "3px 0",
      borderBottom: "1px dashed #e8edf2",
      fontSize: "11.5px",
      color: "#374151",
    },
    infoLabel: { color: "#6b7280", fontWeight: "500", minWidth: "110px" },
    infoValue: { fontWeight: "600", color: "#111827", textAlign: "right" },
    tableWrap: {
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      borderRadius: "6px",
      border: "1px solid #dde5f0",
      marginBottom: "12px",
    },
    table: {
      width: "100%",
      minWidth: "700px",
      borderCollapse: "collapse",
      fontSize: "11.5px",
    },
    thead: { background: BRAND },
    th: {
      color: "#fff",
      fontWeight: "600",
      padding: "7px 9px",
      whiteSpace: "nowrap",
      letterSpacing: "0.3px",
      borderRight: "1px solid rgba(255,255,255,0.15)",
    },
    td: {
      padding: "5px 9px",
      borderBottom: "1px solid #e8edf5",
      verticalAlign: "top",
      color: "#374151",
    },
    totalsBox: {
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: "10px",
    },
    totalsInner: {
      width: "100%",
      maxWidth: "320px",
      background: "#f7faff",
      borderRadius: "6px",
      border: "1px solid #dde5f0",
      overflow: "hidden",
    },
    totalRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 12px",
      borderBottom: "1px solid #e8edf5",
      fontSize: "12px",
      color: "#374151",
    },
    totalRowFinal: {
      display: "flex",
      justifyContent: "space-between",
      padding: "7px 12px",
      background: BRAND,
      color: "#fff",
      fontWeight: "700",
      fontSize: "13px",
    },
    totalLabel: { color: "#6b7280" },
    totalValue: { fontWeight: "600" },
    wordsStrip: {
      background: "#fffbeb",
      border: "1px solid #fcd34d",
      borderRadius: "6px",
      padding: "6px 12px",
      fontSize: "11.5px",
      color: "#92400e",
      fontWeight: "600",
      marginBottom: "10px",
    },
    noteBox: {
      background: "#f0f9ff",
      border: "1px solid #bae6fd",
      borderRadius: "6px",
      padding: "6px 12px",
      fontSize: "11px",
      color: "#0369a1",
      marginBottom: "10px",
    },
    metaRow: {
      display: "flex",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: "6px",
      fontSize: "11px",
      color: "#6b7280",
      marginBottom: "6px",
    },
    metaItem: { display: "flex", gap: "4px" },
    metaValue: { fontWeight: "600", color: "#374151" },
    footer: { background: BRAND, padding: "10px 20px 8px" },
    footerContactRow: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      gap: "16px",
      marginBottom: "4px",
    },
    footerContactItem: {
      display: "flex",
      alignItems: "center",
      gap: "5px",
      color: "rgba(255,255,255,0.95)",
      fontSize: "11.5px",
      fontWeight: "500",
    },
    footerContactIcon: {
      width: "18px",
      height: "18px",
      background: "rgba(255,255,255,0.22)",
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "10px",
    },
    footerAddress: {
      textAlign: "center",
      color: "rgba(255,255,255,0.85)",
      fontSize: "10.5px",
      marginTop: "3px",
    },
    downloadWrap: {
      display: "flex",
      justifyContent: "center",
      padding: "14px 20px 18px",
    },
    downloadBtn: {
      background: isDownloading ? "#9ca3af" : BRAND,
      color: "#fff",
      border: "none",
      borderRadius: "8px",
      padding: "10px 36px",
      fontSize: "14px",
      fontWeight: "700",
      cursor: isDownloading ? "not-allowed" : "pointer",
      letterSpacing: "0.5px",
      boxShadow: isDownloading ? "none" : "0 4px 14px rgba(91,175,214,0.45)",
      transition: "all 0.2s",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
  };

  return (
    <div style={containerStyle}>
      <div style={styles.pageWrap}>
        <div style={{ ...styles.card, marginTop: "0", paddingTop: "0" }}>
          <div
            className="receipt-inner-unique"
            ref={receiptRef}
            style={{ minWidth: "300px", marginTop: "0", paddingTop: "0" }}
          >
            <div className="receipt-header-section" style={styles.header}>
              <div style={styles.headerTop}>
                <div style={styles.logoBox}>
                  {!isZoneLoading && (
                    <img
                      src={
                        zoneData?.data
                          ? `data:image/png;base64,${zoneData.data}`
                          : "../assets/img/medicity_logo.png"
                      }
                      alt="Logo"
                      crossOrigin="anonymous"
                      style={{
                        maxWidth: "180px",
                        maxHeight: "60px",
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        display: "block",
                      }}
                      onError={(e) => {
                        e.target.src = "../assets/img/medicity_logo.png";
                      }}
                    />
                  )}
                </div>
                <div style={styles.headerCenter}>
                  <p style={styles.headerTitle}>Bill cum Cash Receipt</p>
                  <p style={styles.headerSubtitle}>OFFICIAL PAYMENT DOCUMENT</p>
                </div>
                <div>
                  <span style={styles.badgePill}>
                    #{" "}
                    {rowCashReceiptData?.appointment_payment_detail_obj[0]
                      ?.id || "—"}
                  </span>
                </div>
              </div>
              <div style={styles.headerDivider} />
            </div>

            <div style={styles.body}>
              <p style={styles.sectionLabel}>
                Patient &amp; Appointment Details
              </p>
              <div style={styles.infoGrid}>
                <div>
                  {[
                    ["Patient Name", patient_obj?.patient_name || "—"],
                    [
                      "Age / Gender",
                      `${ageDetails?.years} Y / ${patient_obj?.gender || "—"}`,
                    ],
                    ["Contact No.", patient_obj?.mobile || "—"],
                    [
                      "Referred By",
                      rowCashReceiptData?.refer_doctor_name || "—",
                    ],
                    ["Address", patient_obj?.address || "—"],
                    ["Lab Name", rowCashReceiptData?.lab_name || "—"],
                  ].map(([label, value]) => (
                    <div key={label} style={styles.infoRow}>
                      <span style={styles.infoLabel}>{label}</span>
                      <span style={styles.infoValue}>{value}</span>
                    </div>
                  ))}
                </div>
                <div>
                  {[
                    ["Reg No.", patient_obj?.registration_no || "—"],
                    ["Booking ID", rowCashReceiptData?.id || "—"],
                    [
                      "Patient Bill No.",
                      rowCashReceiptData?.appointment_payment_detail_obj[0]
                        ?.id || "—",
                    ],
                    [
                      "Visit / Reg. Date",
                      formatUnixTimestamp(patient_obj?.generated),
                    ],
                    ["Client Name", patient_obj?.agent_name || "—"],
                    ["Center", zone_obj?.zone_name || "—"],
                    ...(membershipCardNo
                      ? [["Membership Card No.", membershipCardNo]]
                      : []),
                  ].map(([label, value]) => (
                    <div key={label} style={styles.infoRow}>
                      <span style={styles.infoLabel}>{label}</span>
                      <span style={styles.infoValue}>{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p style={styles.sectionLabel}>Services</p>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead style={styles.thead}>
                    <tr>
                      {[
                        "#",
                        "Service Code",
                        "Service Name",
                        "Delivery Date",
                        "MRP",
                        ...(membershipCardNo ? ["Discount"] : []),
                        "Total",
                      ].map((h) => (
                        <th key={h} style={styles.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedData).map(
                      ([packageId, data], index) => {
                        const discountAmount = data.mrp - data.package_cost;
                        const isAlt = index % 2 === 1;
                        return (
                          <tr
                            key={packageId}
                            style={isAlt ? { background: "#f7faff" } : {}}
                          >
                            <td style={styles.td}>{index + 1}</td>
                            <td style={styles.td}>
                              {data.investigations
                                .map((inv) => inv?.investigation_code)
                                .join(", ") || "—"}
                            </td>
                            <td style={styles.td}>
                              <div>
                                {data.package?.package_name && (
                                  <strong
                                    style={{
                                      display: "block",
                                      color: BRAND,
                                      marginBottom: "1px",
                                    }}
                                  >
                                    {data.package.package_name}
                                  </strong>
                                )}
                                <span
                                  style={{ color: "#6b7280", fontSize: "11px" }}
                                >
                                  {data.investigations
                                    .map((inv) => inv?.investigation_name)
                                    .join(", ")}
                                </span>
                              </div>
                            </td>
                            <td style={styles.td}>
                              {Array.from(data.delivery_times).join(", ") ||
                                "—"}
                            </td>
                            <td style={{ ...styles.td, fontWeight: "600" }}>
                              {data.mrp ?? "—"}
                            </td>
                            {membershipCardNo && (
                              <td style={{ ...styles.td, color: "#dc2626" }}>
                                {discountAmount > 0 ? discountAmount : 0}
                              </td>
                            )}
                            <td
                              style={{
                                ...styles.td,
                                fontWeight: "700",
                                color: BRAND,
                              }}
                            >
                              {data.package_cost || "—"}
                            </td>
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>

              {appointment_detail_obj[0]?.collection_charge > 0 && (
                <div style={{ marginBottom: "10px" }}>
                  <p style={styles.sectionLabel}>Collection Charges</p>
                  <div
                    style={{
                      ...styles.totalsBox,
                      justifyContent: "flex-start",
                    }}
                  >
                    <div style={{ ...styles.totalsInner, maxWidth: "320px" }}>
                      {[
                        [
                          "Coll. Charges",
                          appointment_detail_obj[0]?.collection_charge || 0,
                        ],
                        [
                          "Received Coll. Charges",
                          appointment_detail_obj[0]
                            ?.collection_charge_received || 0,
                        ],
                        [
                          "Balance Coll. Charges",
                          appointment_detail_obj[0]
                            ?.balance_collection_charge || 0,
                        ],
                      ].map(([label, value]) => (
                        <div key={label} style={styles.totalRow}>
                          <span style={styles.totalLabel}>{label}</span>
                          <span style={styles.totalValue}>{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div style={styles.totalsBox}>
                <div style={styles.totalsInner}>
                  {[
                    [
                      "Bill Amount",
                      appointment_detail_obj[0]?.total_mrp_rate || 0,
                    ],
                    [
                      "Total Discount",
                      (appointment_detail_obj[0]?.discount_amount || 0) +
                        totalItemDiscounts,
                    ],
                    [
                      "Total Payable Amount",
                      appointment_detail_obj[0]?.amount_after_discount || 0,
                    ],
                    [
                      "Total Paid Amount",
                      appointment_detail_obj[0]?.received_amount || 0,
                    ],
                  ].map(([label, value]) => (
                    <div key={label} style={styles.totalRow}>
                      <span style={styles.totalLabel}>{label}</span>
                      <span style={styles.totalValue}>₹ {value}</span>
                    </div>
                  ))}
                  <div style={styles.totalRowFinal}>
                    <span>Due Amount</span>
                    <span>
                      ₹ {appointment_detail_obj[0]?.balance_amount || 0}
                    </span>
                  </div>
                </div>
              </div>

              <div style={styles.wordsStrip}>
                💰 Received with thanks:{" "}
                {totalPaidAmount
                  ? `Rupees ${capitalizeWords(converter.toWords(totalPaidAmount))} only`
                  : "Rupees Zero only"}
              </div>

              <div style={styles.noteBox}>
                <strong>Note:</strong> This is a computer-generated Bill/Receipt
                and does not require any seal or signature.
              </div>

              <div style={styles.metaRow}>
                <div style={styles.metaItem}>
                  <span>Bill Generated By:</span>
                  <span style={styles.metaValue}>
                    {rowCashReceiptData?.created_by_name || "—"}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span>Print Date:</span>
                  <span style={styles.metaValue}>
                    {new Date().toLocaleString("en-IN", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div style={styles.metaItem}>
                  <span>Bill Printed By:</span>
                  <span style={styles.metaValue}>
                    {user?.employee_name || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewRegistrationPatientReceipt;
